import { useEffect, useMemo, useRef, useState } from "react"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetClientQuery } from "store/api/clientApi"
import { ApiTypes, UITypes } from "types"

import { convertEditDevicesToState } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DeviceTabs } from "components/common/DeviceTabs"
import { Spinner } from "components/common/Spinner"
import { Tabs } from "components/common/Tabs"
import { ButtonNew } from "components/form/ButtonNew"

import { CustomerDashboardProvider } from "./CustomerDashboardContext"
import { CustomerDetailsSidebar } from "./CustomerDetailsSidebar"

const DevicesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
`

const StyledDeviceTabs = styled(DeviceTabs)`
  flex: 1;
  min-width: 0;
  margin-bottom: 0px;
`

const CustomerDetailsButton = styled(ButtonNew)`
  background-color: var(--surface-2) !important;
  border: none !important;
  color: var(--accent) !important;
`

const CustomerDashboardContentWrapper = styled(ContentWrapper)`
  display: flex;
  flex-direction: column;
  padding-bottom: 0px !important;

  > div {
    &:first-child {
      span:last-child {
        width: calc(100vw - 160px);
      }
    }
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
`

const OutletWrapper = styled.div<{ sidebarOpen: boolean }>`
  position: relative;
  overflow: hidden;
  padding: 0.1px;
  padding-right: ${({ sidebarOpen }) => (sidebarOpen ? "300px" : "0")};
  transition: padding-right 0.3s ease;
  flex: 1;

  @media (max-width: 700px) {
    padding-right: 0;
  }
`

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

const tabs = [
  { label: "Overview", path: "overview" },
  { label: "Details", path: "details" },
  { label: "Monitoring", path: "monitoring" },
  { label: "Live Data", path: "livedata" },
  { label: "Alerts", path: "alerts" },
  { label: "Messages", path: "messages" },
  { label: "Performance Test", path: "perf-test" },
  { label: "Reports", path: "reports" },
]

export const CustomerDashboardLayout = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [triggerGetClient, { data, error }] = useLazyGetClientQuery()
  const [currentDevice, setCurrentDevice] = useState(0)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [highlightTransfer, setHighlightTransfer] = useState(false)
  const [cachedCustomerData, setCachedCustomerData] = useState<ApiTypes.Model.ClientDevices | null>(null)
  const fetchedCustomerIdRef = useRef<number | null>(null)
  const deviceSelectedFromUrlRef = useRef(false)
  const sidebarAutoOpenedRef = useRef<boolean>(false)

  useEffect(() => {
    if (id) {
      const customerId = parseInt(id, 10)
      if (isNaN(customerId)) {
        toast.error("Invalid customer ID")
        navigate("/customers")
        return
      }

      if (fetchedCustomerIdRef.current !== customerId) {
        triggerGetClient(customerId)
        fetchedCustomerIdRef.current = customerId
        deviceSelectedFromUrlRef.current = false
      }
    }
  }, [id, triggerGetClient, navigate])

  useEffect(() => {
    if (cachedCustomerData && id) {
      const isOverviewPage = location.pathname.endsWith("/overview") || location.pathname === `/customers/${id}`
      const shouldOpenSidebar = new URLSearchParams(location.search).get("openSidebar") === "true"

      if (isOverviewPage && !sidebarAutoOpenedRef.current) {
        setIsSidebarOpen(true)
        sidebarAutoOpenedRef.current = true
      } else if (shouldOpenSidebar) {
        setIsSidebarOpen(true)
        const newUrl = location.pathname
        navigate(newUrl, { replace: true })
      }
    }
  }, [cachedCustomerData, location.pathname, location.search, id, navigate])

  useEffect(() => {
    if (error) {
      const is404Error = "status" in error && error.status === 404

      if (is404Error) {
        toast.error("Customer not found")
        navigate("/customers")
        return
      }

      const errorMessage = getErrorMessage(error)
      if (errorMessage) {
        toast.error(errorMessage)
      }
    }
  }, [error, navigate])

  useEffect(() => {
    if (data?.data) {
      setCachedCustomerData(data.data)
    }
  }, [data, cachedCustomerData, navigate])

  useEffect(() => {
    if (cachedCustomerData) {
      const devicesArray = cachedCustomerData.devices || []
      if (devicesArray.length === 0) {
        toast.error("No devices found for this customer")
        const customerEmail = cachedCustomerData.email
        const isArchived = cachedCustomerData.archived != null
        const urlParams = new URLSearchParams()
        urlParams.set("email", customerEmail)
        if (isArchived) {
          urlParams.set("showArchived", "true")
        }
        navigate(`/customers?${urlParams.toString()}`)
      }
    }
  }, [cachedCustomerData, navigate])

  const devices = useMemo(() => {
    return cachedCustomerData?.devices || []
  }, [cachedCustomerData?.devices])

  useEffect(() => {
    if (devices.length > 0 && !deviceSelectedFromUrlRef.current) {
      const searchParams = new URLSearchParams(location.search)
      const selectedDeviceSN = searchParams.get("selectedDevice")

      if (selectedDeviceSN) {
        const deviceIndex = devices.findIndex(device => device.sn === selectedDeviceSN)

        if (deviceIndex !== -1) {
          setCurrentDevice(deviceIndex)
        } else {
          setCurrentDevice(0)
        }

        searchParams.delete("selectedDevice")
        const newSearch = searchParams.toString()
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
        navigate(newUrl, { replace: true })

        deviceSelectedFromUrlRef.current = true
      }
    }
  }, [devices, location.search, location.pathname, navigate])

  const convertedDevices = useMemo(() => {
    if (!devices.length) return []
    return convertEditDevicesToState(devices)
  }, [devices])

  const breadcrumbs = useMemo((): UITypes.Breadcrumb[] => {
    const customer = cachedCustomerData
    if (!customer) {
      return [
        {
          label: "Customers",
          link: "/customers",
        },
      ]
    }

    const customerLabel = customer.full_name ? `${customer.email} (${customer.full_name})` : customer.email

    return [
      {
        label: "Customers",
        link: "/customers",
      },
      {
        label: customerLabel,
      },
    ]
  }, [cachedCustomerData])

  const handleTabChange = (index: number) => {
    setCurrentDevice(index)
  }

  if (!cachedCustomerData) {
    return (
      <CustomerDashboardContentWrapper>
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      </CustomerDashboardContentWrapper>
    )
  }

  const handleCustomerDetailsClick = () => {
    setIsSidebarOpen(prev => !prev)
  }

  const handleSidebarClose = () => {
    setIsSidebarOpen(false)
  }

  const handleUpdateSuccess = () => {
    if (id) {
      const customerId = parseInt(id, 10)
      if (!isNaN(customerId)) {
        triggerGetClient(customerId)
      }
    }
  }

  if (cachedCustomerData && cachedCustomerData.devices.length === 0) {
    return null
  }

  return (
    <CustomerDashboardProvider
      devices={devices}
      currentDevice={currentDevice}
      setCurrentDevice={setCurrentDevice}
      isSidebarOpen={isSidebarOpen}
      customer={cachedCustomerData}
      onUpdateSuccess={handleUpdateSuccess}
      highlightTransfer={highlightTransfer}
      setHighlightTransfer={setHighlightTransfer}
    >
      <CustomerDashboardContentWrapper breadcrumbs={breadcrumbs}>
        <ContentArea>
          {devices.length > 0 && (
            <DevicesWrapper>
              <StyledDeviceTabs
                devices={convertedDevices}
                currentDevice={currentDevice}
                onTabChange={handleTabChange}
              />
              <CustomerDetailsButton onClick={handleCustomerDetailsClick} size="auto">
                Customer Details
              </CustomerDetailsButton>
            </DevicesWrapper>
          )}
          <Tabs tabs={tabs} basePath={`/customers/${id}`} defaultTab="overview" />
          <OutletWrapper sidebarOpen={isSidebarOpen}>
            <Outlet />
            <CustomerDetailsSidebar
              isOpen={isSidebarOpen}
              customer={cachedCustomerData}
              onClose={handleSidebarClose}
              onUpdateSuccess={handleUpdateSuccess}
            />
          </OutletWrapper>
        </ContentArea>
      </CustomerDashboardContentWrapper>
    </CustomerDashboardProvider>
  )
}
